import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Router, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Wifi,
  Server,
  Activity,
  Clock,
  Building2,
  Search,
  Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuperAdminRouterStatus = () => {
  const [routers, setRouters] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTenant, setFilterTenant] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchRouters = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/superadmin/all-routers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setRouters(response.data.data || []);
        // Extract unique tenants
        const uniqueTenants = [...new Map(
          (response.data.data || [])
            .filter(r => r.tenant_name)
            .map(r => [r.tenant_id, { id: r.tenant_id, name: r.tenant_name }])
        ).values()];
        setTenants(uniqueTenants);
      }
    } catch (error) {
      console.error('Error fetching routers:', error);
      toast.error('Gagal memuat data router');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchRouters();
    // Auto refresh every 30 seconds
    const interval = setInterval(fetchRouters, 30000);
    return () => clearInterval(interval);
  }, [fetchRouters]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchRouters();
  };

  const getStatusBadge = (status, isConnected) => {
    if (status === 'online' || isConnected) {
      return (
        <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          <CheckCircle className="w-3 h-3 mr-1" />
          Online
        </Badge>
      );
    } else if (status === 'offline') {
      return (
        <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <XCircle className="w-3 h-3 mr-1" />
          Offline
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
          <AlertCircle className="w-3 h-3 mr-1" />
          Unknown
        </Badge>
      );
    }
  };

  // Filter routers
  const filteredRouters = routers.filter(router => {
    const matchSearch = searchTerm === '' || 
      router.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      router.host?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      router.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchTenant = filterTenant === 'all' || router.tenant_id === filterTenant;
    
    const matchStatus = filterStatus === 'all' || 
      (filterStatus === 'online' && (router.status === 'online' || router.is_connected)) ||
      (filterStatus === 'offline' && router.status === 'offline' && !router.is_connected);
    
    return matchSearch && matchTenant && matchStatus;
  });

  // Stats
  const totalRouters = routers.length;
  const onlineRouters = routers.filter(r => r.status === 'online' || r.is_connected).length;
  const offlineRouters = routers.filter(r => r.status === 'offline' && !r.is_connected).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-indigo-600 dark:text-indigo-400" />
          <p className="text-slate-600 dark:text-slate-400">Memuat data router...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Router className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            Status Router MikroTik
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Monitoring semua router dari seluruh tenant
          </p>
        </div>
        <Button 
          onClick={handleRefresh} 
          disabled={refreshing}
          className="bg-indigo-600 hover:bg-indigo-700"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-md bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-sm">Total Router</p>
                <p className="text-3xl font-bold">{totalRouters}</p>
              </div>
              <Server className="w-10 h-10 text-indigo-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-md bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Online</p>
                <p className="text-3xl font-bold">{onlineRouters}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-md bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Offline</p>
                <p className="text-3xl font-bold">{offlineRouters}</p>
              </div>
              <XCircle className="w-10 h-10 text-red-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                <Input
                  placeholder="Cari router atau IP..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterTenant} onValueChange={setFilterTenant}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <Building2 className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter Tenant" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Tenant</SelectItem>
                {tenants.map(tenant => (
                  <SelectItem key={tenant.id} value={tenant.id}>{tenant.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-[150px]">
                <Activity className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Status</SelectItem>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Router List */}
      <Card className="border-0 shadow-md">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Wifi className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            Daftar Router ({filteredRouters.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase dark:text-slate-400">Router</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase dark:text-slate-400">Tenant</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase dark:text-slate-400">IP Address</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase dark:text-slate-400">Port</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase dark:text-slate-400">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase dark:text-slate-400">Last Connected</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {filteredRouters.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                      Tidak ada router yang ditemukan
                    </td>
                  </tr>
                ) : (
                  filteredRouters.map((router) => (
                    <tr key={router.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Router className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white text-sm">{router.name}</p>
                            {router.location && (
                              <p className="text-xs text-slate-500 dark:text-slate-400">{router.location}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                          <span className="text-sm text-slate-700 dark:text-slate-300">
                            {router.tenant_name || '-'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-sm bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                          {router.host}
                        </code>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                        {router.port}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(router.status, router.is_connected)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                          <Clock className="w-3 h-3" />
                          {router.last_connected 
                            ? new Date(router.last_connected).toLocaleString('id-ID')
                            : '-'
                          }
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminRouterStatus;
